import { Injectable, inject } from '@angular/core';
import {
  CoreHttpClient,
  CoreMobiResource,
  CoreMobiResourceType,
  CoreInstance,
  selectorCoreInstanceInfo,
  selectorCoreSelectedInstance
} from '@bepatient/core';
import { Platform } from '@ionic/angular';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  Timeline,
  TimelineBlocker,
  TimelineInfoMessage
} from './timeline.models';

/**
 * Service responsable des calls API pour la timeline
 */
@Injectable()
export class TimelineApiMobiService {
  private http = inject(CoreHttpClient);
  private store = inject<Store<any>>(Store);
  private translate = inject(TranslateService);
  private platform = inject(Platform);

  /**
   * Wrapper pour charger la timeline via [realTimeline]{@link #realTimeline}
   */
  loadTimeline(): Observable<Timeline> {
    return this.realTimeline();
  }

  /**
   * Fait un POST pour passer le status d'un message d'info à "done"
   *
   * `POST /api/contents/site-infos/[uuid]{@link TimelineInfoMessage#uuid}`
   *
   * @param infoMessage Le message d'info à passer en "done"
   */
  infoMessageDone(infoMessage: TimelineInfoMessage): Observable<any> {
    return this.http.post(
      `/api/contents/site-infos/${infoMessage.uuid}`,
      {},
      {
        withMobiCredentials: true
      }
    );
  }

  /**
   * Charge la timeline et parse les données retournées par MOBI
   *
   * `GET /api/timeline`
   */
  realTimeline(): Observable<Timeline> {
    return this.http
      .get<any>('/api/timeline', {
        withMobiCredentials: true
      })
      .pipe(
        map<any, any[]>(res =>
          res.items
            .filter(item => {
              return this.platform.is('ios')
                ? item.meta && item.meta.type !== 'activity-model-feetme'
                : true;
            })
            .map(item => ({
              ...item.meta,
              uuid: item.uuid,
              href: item.links && item.links.self && item.links.self.href
            }))
        ),
        switchMap<any[], Observable<any[]>>(items =>
          items.length
            ? forkJoin(
                items.map(item =>
                  item.type !== 'activity-site-info'
                    ? of(item)
                    : this.http
                        .get(`/api/contents/site-infos/${item.uuid}`, {
                          withMobiCredentials: true
                        })
                        .pipe(
                          withLatestFrom(
                            this.store.pipe(
                              select(selectorCoreSelectedInstance)
                            )
                          ),
                          map<[any, CoreInstance], any>(
                            ([res, selectedInstance]) => ({
                              ...item,
                              content:
                                res.meta &&
                                res.meta.html &&
                                res.meta.html.replace(
                                  /src="\//g,
                                  `src="${selectedInstance.url}/`
                                ),
                              buttonLabel: res.meta && res.meta.button_label,
                              showButton: res.meta && res.meta.show_button
                            })
                          )
                        )
                )
              )
            : of([])
        ),
        withLatestFrom(
          this.store.pipe(select(selectorCoreInstanceInfo)),
          (items, instanceInfo) => [
            items,
            (instanceInfo && instanceInfo.appColor) || ''
          ]
        ),
        map<[any[], string], Timeline>(([items, appColor]) => {
          const timeline: Timeline = {
            days: [
              {
                date: moment().format('ddd D MMMM'),
                subDate: this.translate.instant('TIMELINE.TODAY'),
                activities: []
              },
              {
                date: moment().add(-1, 'days').format('ddd D MMMM'),
                subDate: this.translate.instant('TIMELINE.YESTERDAY'),
                activities: []
              },
              {
                date: this.translate.instant('TIMELINE.TWO_DAYS'),
                subDate: this.translate.instant('TIMELINE.OR_MORE'),
                activities: []
              }
            ],
            infoMessages: [],
            blockers: []
          };

          items.forEach(item => {
            if (item.type === 'activity-site-info') {
              const infoMessage = {
                uuid: item.uuid,
                href: item.href,
                color: appColor,
                content: item.content,
                buttonLabel: item.buttonLabel,
                blocking: item.is_blocking ? 1 : 0,
                showButton: item.showButton
              };
              timeline.infoMessages.push(infoMessage);
              if (item.is_blocking) {
                const timelineBlocker: TimelineBlocker = {};
                timelineBlocker.infoMessage = infoMessage;
                timeline.blockers.push(timelineBlocker);
              }
            } else {
              const daysAgo = moment().diff(
                moment(item.ctime).startOf('day'),
                'days'
              );

              const convertResource = (
                type: string,
                href: string,
                uuid: string
              ): CoreMobiResource => {
                switch (type) {
                  case 'activity-form':
                    return { type: CoreMobiResourceType.FORM, href };
                  case 'activity-category':
                    return { type: CoreMobiResourceType.CATEGORY, href };
                  case 'activity-forms-container':
                    return { type: CoreMobiResourceType.CATEGORY, href };
                  case 'activity-fiche':
                    return { type: CoreMobiResourceType.FACTSHEET, href };
                  case 'activity-quizz':
                    return { type: CoreMobiResourceType.QUIZZ, href };
                  case 'activity-chatbot':
                    return { type: CoreMobiResourceType.CHATBOT, href };
                  case 'activity-body-schema-pain':
                    return { type: CoreMobiResourceType.BODY_SCHEMA, href };
                  case 'activity-form-webview':
                    return { type: CoreMobiResourceType.DEEPLINK, href };
                  case 'activity-bepatient-measure-model-discrete':
                  case 'activity-bepatient-model-tension':
                  case 'activity-bepatient-model-activity':
                  case 'activity-bepatient-model-misfit':
                  case 'activity-bepatient-model-activity-ihealth':
                  case 'activity-bepatient-model-blood-glucose':
                  case 'activity-bepatient-computed-measure-model':
                  case 'activity-bepatient-model-enumerate':
                  case 'activity-bepatient-model-fcal':
                  case 'activity-bepatient-scoring':
                  case 'activity-model-feetme':
                    return {
                      type: CoreMobiResourceType.MEASURE,
                      href: '/api/measures/measure/' + uuid
                    };
                  default:
                    return void 0;
                }
              };

              const activity = {
                color: item.color || appColor,
                title: item.title || '',
                image: item.thumbnail || '',
                message: item.description || '',
                blocking: item.is_blocking ? 1 : 0,
                progress: item.percent / 100 || 0,
                resource: convertResource(
                  item.type || '',
                  item.href || '',
                  item.uuid || ''
                )
              };
              timeline.days[daysAgo > 2 ? 2 : daysAgo].activities.push(
                activity
              );
              if (item.is_blocking) {
                const timelineBlocker: TimelineBlocker = {};
                timelineBlocker.activity = activity;
                timeline.blockers.push(timelineBlocker);
              }
            }
          });

          return {
            ...timeline,
            days: timeline.days.filter(day => day.activities.length)
          };
        })
      );
  }

  /**
   * @ignore
   */
  private mockedTimeline(): Observable<Timeline> {
    const uuid = () => Math.round(1000000 * Math.random()).toString(16);
    const rand = () => 50 * Math.ceil(8 * Math.random());
    const randImg = () => `http://via.placeholder.com/${rand()}x${rand()}`;

    return of<Timeline>({
      days: [
        {
          date: 'ven. 5 juillet',
          subDate: 'aujourd hui',
          activities: [
            {
              color: '#FABA8C',
              title: 'Les médicaments',
              image: randImg(),
              message:
                'À travers cette fiche pratique, apprenez-en plus sur les médicaments et sur les différents modes de prise',
              blocking: 1,
              progress: 0.5,
              resource: void 0
            },
            {
              color: '#FC907B',
              title: 'Alimentation équilibrée en sel (normosodée)',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#FC907B',
              title: 'Alimentation équilibrée en sel (normosodée)',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#647AEC',
              title: 'Événements de la semaine',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#FABA8C',
              title: 'Les médicaments',
              image: randImg(),
              message:
                'À travers cette fiche pratique, apprenez-en plus sur les médicaments et sur les différents modes de prise',
              blocking: 1,
              progress: 0.5,
              resource: void 0
            },
            {
              color: '#FC907B',
              title: 'Alimentation équilibrée en sel (normosodée)',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#647AEC',
              title: 'Événements de la semaine',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            }
          ]
        },
        {
          date: 'jeu. 4 juillet',
          subDate: 'hier',
          activities: [
            {
              color: '#647AEC',
              title: 'Quelles mesures avez-vous pris ?',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#647AEC',
              title: 'Quelles mesures avez-vous pris ?',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#647AEC',
              title: 'Événements de la semaine',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#647AEC',
              title: 'Événements de la semaine',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#647AEC',
              title: 'Événements de la semaine',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#FC907B',
              title: 'Quelles mesures avez-vous pris ?',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#FC907B',
              title: 'Quelles mesures avez-vous pris ?',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#647AEC',
              title: 'Quelles mesures avez-vous pris ?',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#647AEC',
              title: 'Événements de la semaine',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#647AEC',
              title: 'Événements de la semaine',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#FC907B',
              title: 'Quelles mesures avez-vous pris ?',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            },
            {
              color: '#FC907B',
              title: 'Quelles mesures avez-vous pris ?',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            }
          ]
        },
        {
          date: 'deux jours',
          subDate: 'ou plus...',
          activities: [
            {
              color: '#647AEC',
              title: 'Symptômes à déclarer',
              image: randImg(),
              message: '',
              blocking: 0,
              progress: 0,
              resource: void 0
            }
          ]
        }
      ],
      infoMessages: [
        {
          uuid: uuid(),
          href: '',
          color: '#6B3CD9',
          content: `<p>Ceci est un message d info simple</p><img src="${randImg()}">`,
          buttonLabel: 'OK',
          blocking: 0,
          showButton: true
        },
        {
          uuid: uuid(),
          href: '',
          color: '#6B3CD9',
          content: `<p>Ceci est un message d info bloquant</p><img src="${randImg()}">`,
          buttonLabel: 'OK',
          blocking: 1,
          showButton: true
        }
      ],
      blockers: []
    });
  }
}
